import { createSelector, EntityState } from '@reduxjs/toolkit'
import { denormalize } from 'normalizr'

import {
  IFilterProfile,
  IFilterProfileLite,
  IContinuousRoute,
  IOperator,
  IState,
  IRegion,
  INetwork,
  ICustomQueryAttribute
} from '$/types'
import { RootState } from 'app/store'
import { filterProfileSchema } from 'app/entities/schema/filterProfileSchema'
import { getDefaultProfileId } from 'common/utils/filter/systemScope'

import {
  IOptionalIdObject,
  ISystemScopeFilterProfileValidationErrors,
  ISystemScopeFilterProfileAPIRequestStatus
} from './types'

export const selectSystemScopeIdContainer = (
  state: RootState
): IOptionalIdObject => state.globalFilter.systemScopeIdContainer

export const selectStagingProfile = (
  state: RootState
): IFilterProfile | undefined => state.globalFilter.stagingProfile

export const selectStagingProfileValidationErrors = (
  state: RootState
): ISystemScopeFilterProfileValidationErrors =>
  state.globalFilter.stagingProfileValidationErrors

export const selectStagingProfileAPIRequestStatus = (
  state: RootState
): ISystemScopeFilterProfileAPIRequestStatus =>
  state.globalFilter.stagingProfileAPIRequestStatus

// #region unused selectors
export const selectFilteredOperatorList = (state: RootState): IOperator[] =>
  state.globalFilter.filteredOperators

export const selectFilteredStateList = (state: RootState): IState[] =>
  state.globalFilter.filteredStates

export const selectFilteredRegionList = (state: RootState): IRegion[] =>
  state.globalFilter.filteredRegions

export const selectAttributesList = (
  state: RootState
): ICustomQueryAttribute[] => state.globalFilter.attributes

export const selectFilteredNetworkLines = (
  state: RootState
): IContinuousRoute[] => state.globalFilter.stagingProfile?.continuousRoutes
// #endregion

export const selectFilteredNetworkList = (state: RootState): INetwork[] =>
  state.globalFilter.filteredNetworks

export const selectEditInProgress = (state: RootState): boolean =>
  state.globalFilter.editInProgress

const selectContainerApplied = (state: RootState): IOptionalIdObject =>
  state.globalFilter.systemScopeIdContainer

const selectAllProfiles = (
  state: RootState
): EntityState<IFilterProfileLite, string> => state.entities.filterProfiles

export const selectSystemScopeMeta = createSelector(
  selectFilteredNetworkLines,
  selectContainerApplied,
  selectAllProfiles,
  (filteredNetworkLines, containerApplied, allProfiles) => {
    const denormalizedFilterProfiles: IFilterProfile[] = denormalize(
      allProfiles.ids,
      [filterProfileSchema],
      { filterProfiles: allProfiles.entities }
    )

    const defaultProfileId = getDefaultProfileId(denormalizedFilterProfiles)

    return {
      wasFilteredScopeApplied:
        containerApplied.id && containerApplied.id !== defaultProfileId,
      numberOfSelected: filteredNetworkLines?.length,
      defaultProfileId: defaultProfileId
    }
  }
)

export const selectAllContinuousRoutes = createSelector(
  selectFilteredNetworkList,
  (filteredNetworkList) =>
    filteredNetworkList.flatMap((network: INetwork) => network.continuousRoutes)
)
